import React, { useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Modal, Alert, Checkbox, Select, Input, Button, Space, Typography, message } from 'antd';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { apiErrorFromResponse, userMessageForError } from '../utils/apiError';
import { formatDdMmYyyy } from '../constants/dashboardTiles';

const { Text } = Typography;

const MIN_PASSWORD_LENGTH = 10;
const MAX_LABEL_LENGTH = 80;

// A tile with no value to show right now. Absent `availability` means the server did not say, so no claim.
const isWithheld = (t) => !!t.availability && t.availability !== 'available';

const EXPIRY_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days (default)' },
  { value: 90, label: '90 days' },
];

/**
 * SPOC-only "generate shareable link" action. Rendered ONLY from ProjectDashboard.jsx's
 * `isSpoc` branch (Role.COMPANY_SPOC), for the SPOC's own already-scoped `activeProject` — see
 * that file's header comment for the persona-gating rationale. Real enforcement is server-side
 * (.claude/design/dashboard-redesign.md §A.3: `company_spoc` only, `company_delegate`
 * explicitly denied, membership-checked against `projectId`) — this component's own gating is a
 * UI nicety, never the boundary (CLAUDE.md §6).
 *
 * HARD BUILD CONDITION (2nd Devil's Advocate pass, dashboard-redesign.md §A.1/§A.2 in the
 * dispatching task): this modal discloses the freeze-at-creation behavior BEFORE the SPOC
 * generates a link — an external Board/CIO reader has no other way to know the numbers are
 * pinned at generation time, not live. The disclosure is shown unconditionally, before any
 * tile-selection UI, not tucked into a footnote after success.
 *
 * Contract (dashboard.controller.ts `projects_id_shared_dashboard_linksPost`):
 *   request:  { visibleTileIds: string[], expiresInDays: 7|30|90, password?: string, label?: string (<=80) }
 *   response: { success: true, data: { link: { id, token, url, expiresAt, pinnedAsOf, label,
 *               passwordProtected, aiTilesAtRisk: tileId[] } } }
 * A tile with no value yet (availability other than `available`, e.g. a stage not started) is marked,
 * unticked by default, and explained: the link is frozen at creation, so it would stay empty for its
 * whole life. The success screen shows the one-time URL and can only be closed with "Done"
 * (backdrop, Escape and the corner X are off) so the URL cannot be lost by accident.
 * `link.url` falls back to `${origin}/shared/${link.token}` (the doc's own stated public URL
 * shape, "Public URL shape") if the backend returns only a bare token.
 */
const ShareDashboardModal = ({ open, onClose, projectId, tiles, onCreated }) => {
  const { instance, accounts } = useMsal();

  // Every tile the API returned for this persona is offered; the server alone rejects a tile that may
  // not be shared (TILE_NOT_SHAREABLE / INVALID_TILE_FOR_PERSONA).
  const shareable = useMemo(() => tiles || [], [tiles]);

  const defaultSelection = () => shareable.filter((t) => !isWithheld(t)).map((t) => t.tileId);
  const [selectedIds, setSelectedIds] = useState(defaultSelection);
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { url, expiresAt, pinnedAsOf }
  const [error, setError] = useState(null);

  const reset = () => {
    setSelectedIds(defaultSelection());
    setLabel('');
    setExpiresInDays(30);
    setPassword('');
    setResult(null);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const toggleTile = (tileId, checked) => {
    setSelectedIds((prev) => (checked ? [...prev, tileId] : prev.filter((id) => id !== tileId)));
  };

  const handleGenerate = async () => {
    if (selectedIds.length === 0) {
      message.warning('Select at least one tile to share.');
      return;
    }
    // Same rule as the server (sharedLink.ts MIN_SHARE_PASSWORD_LENGTH); the server still enforces it.
    if (password.trim() && password.trim().length < MIN_PASSWORD_LENGTH) {
      setError(`The password must be at least ${MIN_PASSWORD_LENGTH} characters, or leave it blank for no password.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account: accounts[0] });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/shared-dashboard-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenResponse.accessToken}` },
        body: JSON.stringify({ visibleTileIds: selectedIds, expiresInDays, password: password || undefined, label: label.trim() || undefined }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) {
        throw apiErrorFromResponse(res, json);
      }
      const link = json.data?.link || json.data || {};
      setResult({
        url: link.url || (link.token ? `${window.location.origin}/shared/${link.token}` : null),
        expiresAt: link.expiresAt,
        pinnedAsOf: link.pinnedAsOf,
        label: link.label || null,
        passwordProtected: typeof link.passwordProtected === 'boolean' ? link.passwordProtected : null,
        aiTilesAtRisk: Array.isArray(link.aiTilesAtRisk) ? link.aiTilesAtRisk : [],
      });
      if (onCreated) onCreated();
    } catch (err) {
      setError(userMessageForError(err, 'Could not generate a shareable link. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      message.success('Link copied.');
    } catch {
      message.warning('Could not copy automatically - select and copy the link manually.');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={result ? undefined : handleClose}
      maskClosable={!result}
      keyboard={!result}
      closable={!result}
      title="Generate a shareable dashboard link"
      footer={
        result ? (
          <Button onClick={handleClose}>Done</Button>
        ) : (
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={handleGenerate}>
              Generate link
            </Button>
          </Space>
        )
      }
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        {/* HARD BUILD CONDITION: freeze-at-creation disclosure, shown BEFORE generation. */}
        <Alert
          type="info"
          showIcon
          data-testid="freeze-disclosure"
          message="This link shows the latest figures calculated up to the moment you generate it."
          description="Each tile shows its own date. It will not update as the project progresses - regenerate the link whenever you need fresh numbers."
        />

        {error && <Alert type="error" showIcon message="Could not generate link" description={error} />}

        {!result && (
          <>
            <div>
              <Text strong>Tiles to include</Text>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {shareable.map((t) => (
                  <Checkbox
                    key={t.tileId}
                    checked={selectedIds.includes(t.tileId)}
                    onChange={(e) => toggleTile(t.tileId, e.target.checked)}
                  >
                    {t.title || t.tileId}
                    {isWithheld(t) && (
                      <Text type="secondary" data-testid={`share-tile-withheld-${t.tileId}`}>
                        {' '}
                        ({t.availability === 'stage_not_reached' ? `not started yet${t.stage?.name ? `: ${t.stage.name}` : ''}` : 'no value yet'})
                      </Text>
                    )}
                  </Checkbox>
                ))}
                {shareable.length === 0 && <Text type="secondary">No shareable tiles available.</Text>}
              </div>
              {shareable.some(isWithheld) && (
                <Alert
                  style={{ marginTop: 8 }}
                  type="warning"
                  showIcon
                  data-testid="share-withheld-warning"
                  message="Some tiles have no value yet and are not selected."
                  description="A link is frozen when you generate it, so a tile with no value now stays empty on that link for its whole life. Generate a new link once those tiles have figures."
                />
              )}
            </div>

            <div>
              <Text strong>Link name (optional)</Text>
              <Input
                style={{ marginTop: 8 }}
                value={label}
                maxLength={MAX_LABEL_LENGTH}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="For example: Board 12-10"
                data-testid="share-link-label"
              />
            </div>

            <div>
              <Text strong>Link expires in</Text>
              <Select style={{ width: '100%', marginTop: 8 }} value={expiresInDays} options={EXPIRY_OPTIONS} onChange={setExpiresInDays} />
            </div>

            <div>
              <Text strong>Password (optional)</Text>
              <Input
                type="password"
                style={{ marginTop: 8 }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`Leave blank for no password (minimum ${MIN_PASSWORD_LENGTH} characters)`}
              />
            </div>
          </>
        )}

        {result && (
          <>
            <Alert
              type="success"
              showIcon
              message={result.label ? `Link generated: ${result.label}` : 'Link generated'}
              description={`Shows the latest figures calculated up to ${formatDdMmYyyy(result.pinnedAsOf)}; each tile shows its own date. Expires ${formatDdMmYyyy(result.expiresAt)}.${
                result.passwordProtected === null ? '' : result.passwordProtected ? ' Password protected.' : ' No password.'
              } Copy the link now: it is shown only here, and you can revoke it later from "My shared links".`}
            />
            {result.aiTilesAtRisk.length > 0 && (
              <Alert
                type="warning"
                showIcon
                data-testid="share-ai-risk-warning"
                message={`These AI tiles will not appear on the public link unless a Lead Advisor has directly reviewed them: ${result.aiTilesAtRisk
                  .map((id) => (tiles || []).find((t) => t.tileId === id)?.title || id)
                  .join(', ')}.`}
                description="Auto-refreshed values are withheld publicly."
              />
            )}
            <div style={{ width: '100%' }}>
              <Input
                readOnly
                value={result.url || ''}
                data-testid="shared-link-url"
                style={{ width: '100%' }}
                onFocus={(e) => e.target.select && e.target.select()}
              />
              <Button style={{ marginTop: 8 }} onClick={handleCopy}>
                Copy link
              </Button>
            </div>
          </>
        )}
      </Space>
    </Modal>
  );
};

export default ShareDashboardModal;
